<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { BUTTON_TYPES } from '../constants/buttonTypes';

const props = defineProps({
  button: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  typeOptions: {
    type: Array,
    required: true,
  },
  formErrors: {
    type: Object,
    default: () => ({}),
  },
  canRemove: {
    type: Boolean,
    default: false,
  },
  config: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:type', 'update:field', 'remove']);

const { t } = useI18n();

// Extract config with defaults
const i18nPrefix = computed(
  () => props.config.i18nPrefix || 'CONTENT_TEMPLATES'
);
const limits = computed(() => props.config.limits || {});
const fieldMapping = computed(
  () =>
    props.config.fieldMapping || {
      title: 'title',
      phone: 'phone',
      code: 'code',
    }
);
const showUrlExample = computed(() => props.config.showUrlExample || false);
const copyCodeShowsText = computed(
  () => props.config.copyCodeShowsText !== false
);

// Field names based on platform
const titleField = computed(() => fieldMapping.value.title || 'title');
const phoneField = computed(() => fieldMapping.value.phone || 'phone');
const codeField = computed(() => fieldMapping.value.code || 'code');

// Computed limits
const getTitleMaxLength = computed(
  () =>
    limits.value.BUTTON_TEXT_MAX_LENGTH ??
    limits.value.CALL_TO_ACTION_BUTTON_TITLE_MAX_LENGTH ??
    25
);

const getUrlMaxLength = computed(
  () =>
    limits.value.URL_MAX_LENGTH ??
    limits.value.CALL_TO_ACTION_URL_MAX_LENGTH ??
    2000
);

const getPhoneMaxLength = computed(
  () =>
    limits.value.PHONE_MAX_LENGTH ??
    limits.value.CALL_TO_ACTION_PHONE_MAX_LENGTH ??
    20
);

const getCodeMaxLength = computed(
  () =>
    limits.value.COPY_CODE_MAX_LENGTH ??
    limits.value.CALL_TO_ACTION_CODE_MAX_LENGTH ??
    15
);

const getMinTTL = computed(
  () =>
    limits.value.MIN_TTL_MINUTES ??
    limits.value.CALL_TO_ACTION_MIN_TTL_MINUTES ??
    1
);

const getMaxTTL = computed(
  () =>
    limits.value.MAX_TTL_MINUTES ??
    limits.value.CALL_TO_ACTION_MAX_TTL_MINUTES ??
    43200
);

const getDefaultTTL = computed(
  () =>
    limits.value.DEFAULT_TTL_MINUTES ??
    limits.value.CALL_TO_ACTION_DEFAULT_TTL_MINUTES ??
    10080
);

// Get button values using the correct field names
const buttonTitle = computed(() => props.button[titleField.value] || '');
const buttonPhone = computed(() => props.button[phoneField.value] || '');
const buttonCode = computed(() => props.button[codeField.value] || '');

// Check if URL has variables (for showing example field)
const urlHasVariable = computed(() => {
  const url = props.button.url || '';
  return url.includes('{{');
});

// Show title field for this button type
const showTitleField = computed(() => {
  if (props.button.type === BUTTON_TYPES.COPY_CODE) {
    return copyCodeShowsText.value;
  }
  return true;
});

const handleTypeChange = value => {
  emit('update:type', value);
};

const handleFieldChange = (field, value) => {
  emit('update:field', field, value);
};

const handleRemove = event => {
  event?.stopPropagation();
  emit('remove');
};
</script>

<template>
  <div
    class="flex items-start gap-2 p-4 rounded-lg border border-n-weak bg-n-solid-1"
  >
    <div class="flex-1 flex flex-col gap-3">
      <!-- Button type selector -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{
            t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.BUTTON_TYPE_LABEL`, {
              number: index + 1,
            })
          }}
        </label>
        <ComboBox
          :model-value="button.type"
          :options="typeOptions"
          :searchable="false"
          @update:model-value="handleTypeChange"
        />
      </div>

      <!-- Title/Text field (common to most types) -->
      <Input
        v-if="showTitleField"
        :model-value="buttonTitle"
        :label="
          t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.BUTTON_TITLE_LABEL`, {
            number: index + 1,
          })
        "
        :placeholder="
          t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.BUTTON_TITLE_PLACEHOLDER`)
        "
        :message="formErrors[`button_${index}_${titleField}`]"
        :message-type="
          formErrors[`button_${index}_${titleField}`] ? 'error' : 'info'
        "
        :max-length="getTitleMaxLength"
        @update:model-value="value => handleFieldChange(titleField, value)"
      />

      <!-- URL field -->
      <Input
        v-if="button.type === BUTTON_TYPES.URL"
        :model-value="button.url"
        :label="t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.URL_LABEL`)"
        :placeholder="t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.URL_PLACEHOLDER`)"
        :message="formErrors[`button_${index}_url`]"
        :message-type="formErrors[`button_${index}_url`] ? 'error' : 'info'"
        :max-length="getUrlMaxLength"
        @update:model-value="value => handleFieldChange('url', value)"
      />

      <!-- URL Variable Example (for WhatsApp) -->
      <Input
        v-if="
          button.type === BUTTON_TYPES.URL && showUrlExample && urlHasVariable
        "
        :model-value="button.example"
        :label="t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.URL_EXAMPLE_LABEL`)"
        :placeholder="
          t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.URL_EXAMPLE_PLACEHOLDER`)
        "
        :message="formErrors[`button_${index}_example`]"
        :message-type="formErrors[`button_${index}_example`] ? 'error' : 'info'"
        @update:model-value="value => handleFieldChange('example', value)"
      />

      <!-- Phone field -->
      <div
        v-if="button.type === BUTTON_TYPES.PHONE_NUMBER"
        class="flex flex-col gap-1"
      >
        <Input
          :model-value="buttonPhone"
          :label="t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.PHONE_LABEL`)"
          :placeholder="
            t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.PHONE_PLACEHOLDER`)
          "
          :message="formErrors[`button_${index}_phone`]"
          :message-type="formErrors[`button_${index}_phone`] ? 'error' : 'info'"
          :max-length="getPhoneMaxLength"
          @update:model-value="value => handleFieldChange(phoneField, value)"
        />
        <span class="text-xs text-n-slate-11">
          {{ t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.PHONE_HELP`) }}
        </span>
      </div>

      <!-- Copy Code field -->
      <div
        v-if="button.type === BUTTON_TYPES.COPY_CODE"
        class="flex flex-col gap-1"
      >
        <Input
          :model-value="buttonCode"
          :label="t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.CODE_LABEL`)"
          :placeholder="
            t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.CODE_PLACEHOLDER`)
          "
          :message="formErrors[`button_${index}_code`]"
          :message-type="formErrors[`button_${index}_code`] ? 'error' : 'info'"
          :max-length="getCodeMaxLength"
          @update:model-value="value => handleFieldChange(codeField, value)"
        />
        <span class="text-xs text-n-slate-11">
          {{ t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.CODE_HELP`) }}
        </span>
      </div>

      <!-- Voice Call TTL field -->
      <div
        v-if="button.type === BUTTON_TYPES.VOICE_CALL"
        class="flex flex-col gap-1"
      >
        <Input
          :model-value="button.ttl_minutes"
          type="number"
          :label="t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.TTL_LABEL`)"
          :placeholder="
            t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.TTL_PLACEHOLDER`)
          "
          :message="formErrors[`button_${index}_ttl`]"
          :message-type="formErrors[`button_${index}_ttl`] ? 'error' : 'info'"
          :min="getMinTTL"
          :max="getMaxTTL"
          @update:model-value="value => handleFieldChange('ttl_minutes', value)"
        />
        <span class="text-xs text-n-slate-11">
          {{
            t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.TTL_HELP`, {
              default: getDefaultTTL,
            })
          }}
        </span>
      </div>
    </div>

    <!-- Remove button -->
    <button
      v-if="canRemove"
      type="button"
      class="mt-8 p-2 rounded-lg hover:bg-n-red/10 text-n-red transition-colors"
      :aria-label="t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.REMOVE_BUTTON`)"
      @click="handleRemove"
    >
      <i class="i-lucide-trash-2 text-base" />
    </button>
  </div>
</template>
